'use client';
import Button from '@repo/ui/src/components/Button';
import ShipmentIcon from '@repo/ui/src/components/Icons/ShipmentIcon';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { useOrderShipments } from '@hooks/order/useOrderShipments';
import { useCurrencyString } from '@hooks/useCurrencyString';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useDateLocalizer } from '@hooks/useDateLocalizer';
import { linkClicked, viewedOrderDetails } from '@repo/ui/src/services/analytics';
import {
  getOrderDiscount,
  getOrderProducts,
  getOrderRevenue,
} from '@repo/ui/src/services/analytics/trackOrder';
import { track } from '@repo/ui/src/services/analytics/utils';
import { LinkProps } from '@repo/ui/src/types/link';
import { Order } from '@repo/ui/src/types/order';
import { classNames } from '@repo/ui/src/utils/classNames';
import OrderRowBody from '@views/Account/OrderRowBody';
import dynamic from 'next/dynamic';
import React from 'react';
import type { Options } from 'react-markdown';
import OrderDetailCostSummary from './OrderDetailCostSummary';
import OrderDetailCustomerSummary from './OrderDetailCustomerSummary';
import ShipmentStatus, { ShipmentStatuses } from './ShipmentStatus';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

interface Status {
  title: string;
  description: string;
}
export interface OrderDetailProps {
  order: Order;
  orderNumberText: string;
  orderDateText: string;
  orderHistoryLink: LinkProps;
  giftCardLink: LinkProps;
  trackLinkText: string;
  statuses: Array<Status>;
  digitalStatus: Status;
  reviewText: string;
  quantityText: string;
  helpText: string;
  isAccountPage?: boolean;
  trackOrderDetail: boolean;
}

const components: Options['components'] = {
  p: ({ children }) => (
    <p className="tra-text-web-body-m tw-font-bold tw-no-underline">{children}</p>
  ),
};

const OrderDetailContent = ({
  order,
  orderNumberText,
  orderDateText,
  helpText,
  orderHistoryLink,
  statuses,
  digitalStatus,
  reviewText,
  quantityText,
  trackLinkText,
  giftCardLink,
  isAccountPage = false,
  trackOrderDetail,
}: OrderDetailProps) => {
  const { locale } = useCurrentUrl();
  const getCurrencyString = useCurrencyString();
  const dateLocalizer = useDateLocalizer();
  const orderShipments = useOrderShipments(order);

  React.useEffect(() => {
    if (!trackOrderDetail) {
      return;
    }
    const details = order.details;
    track(viewedOrderDetails, {
      // not really the core purpose of classNames, but it accomplishes what we need
      billing_address: classNames(
        details?.billingAddress?.address1,
        details?.billingAddress?.address2,
      ),
      billing_city: details?.billingAddress?.city,
      billing_state: details?.billingAddress?.stateCode,
      billing_country: details?.billingAddress?.countryCode,
      billing_zip_code: details?.billingAddress?.postalCode,
      brand: 'Traeger',
      locale,
      coupon: details?.couponItems?.map((item) => item.code),
      currency: details?.currency,
      discount: getOrderDiscount(details?.productItems),
      email: details?.customerInfo.email,
      first_name: details?.firstName,
      last_name: details?.lastName,
      order_date: details?.creationDate,
      order_id: details?.orderNo,
      order_status: details?.status,
      packages: details?.packages?.length,
      payment_method: details?.paymentInstruments?.[0]?.paymentCard?.cardType,
      products: getOrderProducts(details?.productItems, details?.giftCertificateItems),
      revenue: getOrderRevenue(details?.productItems),
      shipping: details?.shippingTotal,
      tax: details?.taxTotal,
      total: details?.orderTotal,
    });
  }, [order, trackOrderDetail, locale]);

  return (
    <section className="tw-grid tw-gap-y-14 tw-my-20" data-testid="order-detail">
      <div className="tw-grid tw-grid-flow-col tw-justify-between tw-items-start tw-gap-x-6">
        <div className="tw-grid tw-gap-y-4">
          <h4 className="tra-text-web-heading-4">
            {orderNumberText} #{order.details.orderNo}
          </h4>
          <div className="tw-grid tw-gap-2">
            <p className="tra-text-web-body-m tw-font-bold">
              {orderDateText} {dateLocalizer(order.details.creationDate)}
            </p>
            {helpText && (
              <div className="tw-grid tw-gap-x-3 tw-grid-flow-col tw-items-center tw-justify-start">
                <ShipmentIcon className="tw-w-6 tw-text-brand-300" />
                <Markdown components={components}>{helpText}</Markdown>
              </div>
            )}
          </div>
        </div>
        {isAccountPage && orderHistoryLink && (
          <Anchor {...orderHistoryLink} className="tra-text-web-body-m-link tw-font-bold" />
        )}
      </div>
      <ul className="tw-grid tw-gap-y-6 tw-p-0">
        {orderShipments?.map((shipment, index) => {
          const status =
            shipment.shipmentStatus === -1 ? digitalStatus : statuses[shipment.shipmentStatus];
          return (
            <li
              key={index}
              className="tw-grid tw-gap-y-10 tw-bg-warm-100 tw-border-warm-400 tw-px-4 tw-py-5 tw-mb-7 sm:tw-p-10"
            >
              <div className="tw-grid tw-justify-between tw-items-start tw-grid-flow-col tw-gap-x-6 tw-border-b tw-border-warm-400 tw-pb-5">
                <div className="tw-grid tw-gap-y-4 tw-justify-items-start tw-max-w-1/2">
                  <p className=" tw-bg-warm-200 tra-text-web-utl-link tw-py-1 tw-px-4">
                    {status.title}
                  </p>
                  {status.description && (
                    <p className="tra-text-web-body-s">{status.description}</p>
                  )}
                </div>
                {shipment.trackingUrl && (
                  <Button
                    as={Anchor}
                    externalLink={shipment.trackingUrl}
                    isExternal
                    onClick={() => {
                      linkClicked({
                        affiliation: 'website',
                        brand: 'Traeger',
                        locale,
                        link_location: isAccountPage
                          ? 'my account_order details'
                          : 'order_tracking',
                        link_text: trackLinkText,
                        link_type: 'Internal',
                        link_url: shipment.trackingUrl ?? '',
                      });
                    }}
                  >
                    {trackLinkText}
                  </Button>
                )}
              </div>
              {shipment.shipmentStatus > -1 && (
                <ShipmentStatus statuses={statuses} currentStatus={shipment.shipmentStatus} />
              )}
              <OrderRowBody
                productItems={shipment.productItems}
                giftCertificateItems={shipment.giftCertificateItems ?? []}
                getCurrencyString={getCurrencyString}
                reviewLinkText={reviewText}
                giftCardLink={giftCardLink}
                quantityText={quantityText}
                hideReviewLink={shipment.shipmentStatus !== ShipmentStatuses.delivered}
                isAccountPage={isAccountPage}
              />
            </li>
          );
        })}
      </ul>
      <div className="tw-grid lg:tw-grid-cols-[4fr_3fr] tw-gap-6">
        <OrderDetailCustomerSummary orderDetails={order.details} />
        <OrderDetailCostSummary orderDetails={order.details} />
      </div>
    </section>
  );
};

export default OrderDetailContent;
